import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Calendar, MessageSquare, Bell, BarChart, TrendingUp, Zap } from 'lucide-react';

export function CalendarMockup() {
  return (
    <div className="rounded-lg border border-gray-800 overflow-hidden bg-gray-900/70 shadow-xl">
      <div className="p-3 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center">
          <Calendar className="h-4 w-4 text-blue-400 mr-2" />
          <span className="text-sm font-medium text-gray-200">Economic Calendar</span>
        </div>
        <div className="flex items-center">
          <div className="h-2 w-2 bg-green-500 rounded-full mr-1.5"></div>
          <span className="text-xs text-gray-400">Live</span>
        </div>
      </div>
      <div className="p-3">
        <div className="space-y-3">
          {/* Calendar Events */}
          {[
            { time: '08:30', event: 'US Jobless Claims', impact: 'high', forecast: '220K', previous: '215K' },
            { time: '10:00', event: 'FOMC Meeting', impact: 'high', forecast: '5.25%', previous: '5.0%' },
            { time: '14:30', event: 'ECB Rate Decision', impact: 'medium', forecast: '3.75%', previous: '3.75%' }
          ].map((item, index) => (
            <div 
              key={index} 
              className={`p-2 rounded border flex items-center space-x-2 ${
                item.impact === 'high' 
                  ? 'border-red-800/40 bg-red-900/10' 
                  : 'border-yellow-800/40 bg-yellow-900/10'
              }`}
            >
              <div className="text-center">
                <div className="text-xs font-medium text-gray-300">{item.time}</div>
                <div className={`text-xs ${
                  item.impact === 'high' ? 'text-red-400' : 'text-yellow-400'
                }`}>
                  {item.impact}
                </div>
              </div>
              <div className="flex-1">
                <div className="text-xs font-medium text-gray-200">{item.event}</div>
                <div className="text-xs text-gray-400">
                  Forecast: {item.forecast} | Previous: {item.previous}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-500" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AlertsMockup() {
  return (
    <div className="rounded-lg border border-gray-800 overflow-hidden bg-gray-900/70 shadow-xl">
      <div className="p-3 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center">
          <Bell className="h-4 w-4 text-amber-400 mr-2" />
          <span className="text-sm font-medium text-gray-200">Market Alerts</span>
        </div>
        <div className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-xs">3 New</div>
      </div>
      <div className="p-3">
        <div className="space-y-3">
          {/* Alert Items */}
          {[
            { title: 'EUR/USD breaks resistance', time: '10 min ago', desc: 'EUR/USD broke above 1.0850, watch for potential continuation.', icon: <TrendingUp className="h-4 w-4 text-green-400" /> },
            { title: 'Bitcoin volatility spike', time: '25 min ago', desc: 'BTC volatility increased by 15% in the last hour.', icon: <Zap className="h-4 w-4 text-amber-400" /> },
            { title: 'TSLA earnings report', time: '1 hour ago', desc: 'Tesla beat earnings expectations by 12%, stock up 3% AH.', icon: <BarChart className="h-4 w-4 text-blue-400" /> }
          ].map((alert, index) => (
            <div 
              key={index} 
              className="p-2 rounded border border-gray-800/60 bg-gray-800/20 flex items-start space-x-2"
            >
              <div className="mt-0.5">{alert.icon}</div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <div className="text-xs font-medium text-gray-200">{alert.title}</div>
                  <div className="text-xs text-gray-500">{alert.time}</div>
                </div>
                <div className="text-xs text-gray-400 mt-1">{alert.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ChatMockup() {
  return (
    <div className="rounded-lg border border-gray-800 overflow-hidden bg-gray-900/70 shadow-xl">
      <div className="p-3 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center">
          <MessageSquare className="h-4 w-4 text-emerald-400 mr-2" />
          <span className="text-sm font-medium text-gray-200">AI Market Analyst</span>
        </div>
        <motion.div 
          className="h-2 w-2 bg-emerald-500 rounded-full"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </div>
      <div className="p-3">
        <div className="space-y-3">
          {/* Chat Messages */}
          <div className="p-2 rounded bg-gray-800/40 text-xs text-gray-300">
            <span className="text-emerald-400 font-medium">You: </span>
            How is USD/JPY performing today and what's driving the movement?
          </div>
          
          <div className="p-2 rounded bg-emerald-900/20 border border-emerald-800/30 text-xs text-gray-300">
            <span className="text-emerald-400 font-medium">Strykr AI: </span>
            USD/JPY is up 0.4% today, trading at 142.85. The movement is primarily driven by:
            
            <ul className="mt-1 space-y-1 text-gray-400 pl-4">
              <li>• Strong US retail sales data released this morning</li>
              <li>• Comments from Fed officials suggesting rates may remain higher</li>
              <li>• Technical breakout above the 142.50 resistance level</li>
            </ul>
            
            <div className="mt-2">
              The pair is now approaching the 143.50 resistance - a break above could signal further upside.
            </div>
          </div>
        </div>
        
        <div className="mt-3 pt-2 border-t border-gray-800/50">
          <div className="bg-gray-800/50 rounded border border-gray-700/50 p-1.5 text-xs text-gray-500 flex items-center">
            Ask about any market...
            <div className="ml-auto flex">
              <motion.div 
                className="h-1.5 w-1.5 bg-emerald-500 rounded-full mx-0.5"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0 }}
              />
              <motion.div 
                className="h-1.5 w-1.5 bg-emerald-500 rounded-full mx-0.5"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.3 }}
              />
              <motion.div 
                className="h-1.5 w-1.5 bg-emerald-500 rounded-full mx-0.5"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.6 }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
